/**
 * Everything you have made, in one file.
 *
 * Signed out, all of it lives in this browser and nowhere else. Clearing site
 * data, switching phones, or a browser reclaiming space is enough to lose the
 * lot, and until this existed only the binder could be got out.
 *
 * Accounts arrived later and cover the same ground for people who want them,
 * but this stays the answer for people who do not: a file you own, that works
 * between devices and browsers, and that costs no sign-up and no server copy.
 * It is also the only way to hold on to two collections at once, which is what
 * the first-sign-in prompt points people here for.
 */

import { readStamps, writeStamps } from "./stamps";

export const FORMAT = "pokepricer-backup";
export const VERSION = 1;

/**
 * What is worth keeping, and nothing else.
 *
 * Caches are excluded — they are a copy of somebody else's data and they come
 * back on their own. So is the dev unlock: it is a property of this device,
 * not of the collection, and carrying it into a file would quietly hand it to
 * whoever the file was sent to.
 */
export const KEYS = [
  { key: "ppt_collection_v1", label: "Collection" },
  { key: "ppt_lists_v1", label: "Lists" },
  { key: "ppt_wantlist_v1", label: "Want list" },
  { key: "ppt_goals_v1", label: "Goals" },
  { key: "ppt_binder_v1", label: "Binder" },
  { key: "ppt_submissions_v1", label: "Submissions" },
  { key: "ppt_percentage_v1", label: "Your percentage" },
  { key: "ppt_bulk_filter_v1", label: "Bulk filter" },
  { key: "ppt_hide_oversize_v1", label: "Oversize filter" },
  { key: "ppt_game_v1", label: "Game" },
];

const KNOWN = new Set(KEYS.map((k) => k.key));

/* Stored values are a mix: some keys hold JSON, some hold a bare string like
   "pokemon" or "80". Parsing what parses and leaving the rest alone keeps the
   file readable without breaking the ones that were never JSON — and the write
   side reverses it exactly. */
const decode = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};
const encode = (value) => (typeof value === "string" ? value : JSON.stringify(value));

/** How many cards, lists and so on a set of entries actually holds, so a
 *  restore can say what it is about to bring in rather than just "some data". */
export function summarise(entries) {
  const count = (v) => (Array.isArray(v) ? v.length : null);
  const out = [];
  const cards = count(entries.ppt_collection_v1);
  const lists = count(entries.ppt_lists_v1);
  const wants = count(entries.ppt_wantlist_v1);
  const goals = count(entries.ppt_goals_v1);
  const slots = entries.ppt_binder_v1?.slots
    ? Object.keys(entries.ppt_binder_v1.slots).length
    : null;
  const subs = count(entries.ppt_submissions_v1);

  if (cards) out.push(`${cards} card${cards === 1 ? "" : "s"} in the collection`);
  if (lists) out.push(`${lists} list${lists === 1 ? "" : "s"}`);
  if (wants) out.push(`${wants} on the want list`);
  if (goals) out.push(`${goals} goal${goals === 1 ? "" : "s"}`);
  if (slots) out.push(`${slots} card${slots === 1 ? "" : "s"} in the binder`);
  if (subs) out.push(`${subs} submission${subs === 1 ? "" : "s"}`);
  return out;
}

/** Everything currently saved, ready to be written to a file. */
export function collect(store = window.localStorage) {
  const entries = {};
  for (const { key } of KEYS) {
    const raw = store.getItem(key);
    if (raw != null) entries[key] = decode(raw);
  }
  /* When each of those was last changed, carried beside them rather than
     inside, because it describes the entries rather than being one of them.
     A backup without this is a backup that forgets its own age. */
  const all = readStamps(store);
  const stamps = {};
  for (const { key } of KEYS) {
    if (key in entries && typeof all[key] === "string") stamps[key] = all[key];
  }
  return {
    format: FORMAT,
    version: VERSION,
    savedAt: new Date().toISOString(),
    entries,
    stamps,
  };
}

/**
 * A file back into something that can be written.
 *
 * @returns {{ entries, summary, keys }} or {{ error }} — never half of one.
 *   A partly restored backup is worse than none: the collection could come
 *   from today and the lists from a month ago, and nothing on screen would
 *   say so.
 */
export function parse(raw) {
  let data = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch {
      return { error: "That is not a backup — it isn't even JSON." };
    }
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { error: "That file has nothing in it that looks like a backup." };
  }
  if (data.format && data.format !== FORMAT) {
    return { error: `That is a ${data.format} file, not a backup of everything.` };
  }
  if (!data.entries || typeof data.entries !== "object" || Array.isArray(data.entries)) {
    return { error: "That backup has nothing saved in it." };
  }

  /* Only keys this app knows. A backup is written straight into storage, so
     anything unrecognised in one is either from a newer version or was put
     there by somebody else — neither is worth writing blind. */
  const entries = {};
  let skipped = 0;
  for (const [key, value] of Object.entries(data.entries)) {
    if (KNOWN.has(key)) entries[key] = value;
    else skipped += 1;
  }

  const keys = Object.keys(entries);
  if (!keys.length) {
    return { error: "Nothing in that backup belongs to this app." };
  }

  /* A file written before stamps existed has none, and the moment it was
     saved is the best evidence there is of when its contents were true. */
  const savedAt = typeof data.savedAt === "string" ? data.savedAt : null;
  const given = data.stamps && typeof data.stamps === "object" ? data.stamps : {};
  const stamps = {};
  for (const key of keys) {
    const at = typeof given[key] === "string" ? given[key] : savedAt;
    if (at) stamps[key] = at;
  }

  return { entries, keys, skipped, savedAt, stamps, summary: summarise(entries) };
}

/**
 * Write a parsed backup into storage.
 *
 * Replaces rather than merges, and clears the keys the backup does not carry.
 * Merging means deciding what happens where two collections disagree, and any
 * answer to that is a surprise to somebody; leaving old keys behind is worse
 * still, because the result is a state that was never real.
 */
export function apply(entries, store = window.localStorage, stamps = null) {
  for (const { key } of KEYS) {
    if (Object.prototype.hasOwnProperty.call(entries, key)) {
      store.setItem(key, encode(entries[key]));
    } else {
      store.removeItem(key);
    }
  }

  /* The backup's own dates, not now. Restoring a file from last month does not
     make its contents a month newer, and once there is a server copy to
     compare against, saying otherwise is how a restore would silently
     overwrite work done since. Nothing here is uploaded — but the note has to
     be right before anything reads it. */
  if (stamps) writeStamps({ ...stamps }, store);
}

/** A filename with the date in it, because the whole point is having more
 *  than one and being able to tell them apart. */
export function fileName(at = new Date()) {
  const d = at.toISOString().slice(0, 10);
  return `pokepricer-${d}.backup.json`;
}
